import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { PageContainer } from "@/features/ui";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  PurchasePaymentPdf,
  GeneralInformationSection,
  PurchaseInvoicePaymentsSection,
} from "@/features/purchase-payments";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  ResponsiveToolbarButton,
} from "@/features/ui";

export const PurchasePayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchasePayment, setPurchasePayment] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadPurchasePayment = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`purchase-payments/${referenceNumber}`, {
        params: {
          withCreator: true,
          withBuyer: true,
          withSeller: true,
          withOutletChartOfAccountTransaction: true,
          withCashBankOutletChartOfAccount: true,
          withPurchaseInvoicePayments: true,
        },
      });
      setPurchasePayment(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat pembayaran pembelian")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadPurchasePayment();
  }, [loadPurchasePayment]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.UPDATE_PURCHASE_PAYMENTS) && (
        <ResponsiveToolbarButton
          label="Ubah"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        />
      )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Pembayaran Pembelian"
      breadcrumbs={[
        {
          title: "Pembayaran Pembelian",
          path: "/procurement/purchase-payments",
        },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection purchasePayment={purchasePayment} />
        <PurchaseInvoicePaymentsSection
          purchaseInvoicePayments={purchasePayment.purchaseInvoicePayments}
        />

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Pembayaran Pembelian - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {purchasePayment && user && (
              <PDFViewer width="100%" height="100%">
                <PurchasePaymentPdf
                  purchasePayment={purchasePayment}
                  user={user}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>

        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="PURCHASE_PAYMENT"
          transactionableId={purchasePayment?.id}
        />
      </Stack>
    </PageContainer>
  );
};
